import React, { useRef, useState } from 'react';
import { Box, Divider, Typography, IconButton, Popover } from '@mui/material';
import CircleProgressButton from '../../../../../../buttons/CircleProgressButton';
import { EMOJIS } from '../constants/EmojiConstants';
import { getEmojiImagePath } from '../../../../../../../utilities/emojiUtils';
import SkinTonePicker from './SkinTonePicker';

const styles = {
    popoverPaper: {
        width: 360, height: 440, mt: 1, borderRadius: 1,
        boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden'
    },
    header: { 
        p: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' 
    },
    navContainer: { 
        display: 'flex', px: 1, pb: 1, gap: 0.5, overflowX: 'auto',
        scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }
    },
    scrollContainer: { 
        flex: 1, overflowY: 'auto', p: 1,
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 1 }
    },
    categoryTitle: { 
        display: 'block', px: 1, py: 0.5, fontWeight: 800, color: 'text.disabled',
        textTransform: 'uppercase', position: 'sticky', top: -8, bgcolor: 'background.paper', zIndex: 2
    },
    emojiGrid: { 
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 
    },
    emojiButton: {
        border: 'none',
        cursor: 'pointer',
        p: 1,
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 44,
        height: 44,
        transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            transform: 'scale(1.15)'
        },
        '&:active': {
            transform: 'scale(0.95)'
        }
    },
    footer: { 
        p: 1.5, bgcolor: '#313236', borderTop: '1px solid', borderColor: 'divider', 
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' 
    },
    selectedBox: { 
        width: 36, height: 36, bgcolor: '#313236', borderRadius: 1.5, 
        display: 'flex', alignItems: 'center', justifyContent: 'center', 
        border: '1px solid', borderColor: 'divider' 
    },
    doneButton: { 
        borderRadius: 1, px: 3, py: 0.75, fontWeight: 700, textTransform: 'uppercase' 
    }
};

const EmojiPickerPopover = ({ isOpen, anchorEl, onClose, onDone, selectedEmojiUnicode, onSelect, uniqueId }) => {
    const scrollContainerRef = useRef(null);
    const [skinTonePickerState, setSkinTonePickerState] = useState({
        isOpen: false,
        anchorEl: null,
        baseUnicodeId: null
    });

    const handleCategoryScroll = (catIndex) => {
        const element = document.getElementById(`emoji-cat-${uniqueId}-${catIndex}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleEmojiClick = (event, emojiData) => {
        if (emojiData.hasVariants) {
            setSkinTonePickerState({
                isOpen: true,
                anchorEl: event.currentTarget,
                baseUnicodeId: emojiData.unicode
            });
        } else {
            onSelect(emojiData.unicode);
        }
    };

    const handleSkinToneSelect = (unicode) => {
        onSelect(unicode);
    };

    const handleSkinTonePickerClose = () => {
        setSkinTonePickerState({
            isOpen: false,
            anchorEl: null,
            baseUnicodeId: null
        });
    };

    return (
        <Popover
            open={isOpen}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: styles.popoverPaper }}
        >
            {/* Header */}
            <Box sx={styles.header}>
                <Typography variant="subtitle2" fontWeight={500}>Categories</Typography>
            </Box>

            {/* Category Navigation */}
            <Box sx={styles.navContainer}>
                {EMOJIS.map((cat, i) => {
                    const categoryIcon = cat.emojis[0]?.unicode || '1f600';
                    return (
                        <IconButton key={i} size="small" onClick={() => handleCategoryScroll(i)} sx={{ p: 1, width: 34, height: 34 }}>
                            <img 
                                src={getEmojiImagePath(categoryIcon)} 
                                alt={cat.category} 
                                loading="lazy" 
                                decoding="async"
                                style={{ width: 20, height: 20, display: 'block' }} 
                            />
                        </IconButton>
                    );
                })}
            </Box>
            
            <Divider />

            {/* Emoji Grid */}
            <Box ref={scrollContainerRef} sx={styles.scrollContainer}>
                {EMOJIS.map((category, catIndex) => (
                    <Box key={catIndex} id={`emoji-cat-${uniqueId}-${catIndex}`} sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={styles.categoryTitle}>
                            {category.category}
                        </Typography>
                        <Box sx={styles.emojiGrid}>
                            {category.emojis.map((emojiData, emojiIndex) => {
                                const isSelected = selectedEmojiUnicode === emojiData.unicode;
                                
                                return (
                                    <Box
                                        key={emojiIndex}
                                        component="button"
                                        onClick={(e) => handleEmojiClick(e, emojiData)}
                                        sx={{
                                            ...styles.emojiButton,
                                            bgcolor: isSelected ? 'rgba(255, 255, 255, 0.08)' : 'transparent'
                                        }}
                                        aria-label={emojiData.emoji}
                                    >
                                        <img 
                                            src={getEmojiImagePath(emojiData.unicode)} 
                                            alt={emojiData.emoji} 
                                            loading="lazy" 
                                            decoding="async"
                                            style={{ width: 28, height: 28, display: 'block' }} 
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                ))}
            </Box>

            {/* Footer */}
            <Box sx={styles.footer}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={styles.selectedBox}>
                        {selectedEmojiUnicode && (
                            <img 
                                src={getEmojiImagePath(selectedEmojiUnicode)} 
                                alt="selected" 
                                loading="eager" 
                                decoding="async"
                                style={{ width: 28, height: 28, display: 'block' }} 
                            />
                        )}
                    </Box>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">Selected</Typography>
                </Box>
                <CircleProgressButton
                    onClick={onDone}
                    loading={false}
                    variant='outlined'
                    color='inherit'
                    size="small"
                    sx={styles.doneButton}
                >
                    Done
                </CircleProgressButton>
            </Box>

            {/* Skin Tone Picker */}
            <SkinTonePicker
                isOpen={skinTonePickerState.isOpen}
                anchorEl={skinTonePickerState.anchorEl}
                onClose={handleSkinTonePickerClose}
                baseUnicodeId={skinTonePickerState.baseUnicodeId}
                selectedUnicodeId={selectedEmojiUnicode}
                onSelect={handleSkinToneSelect}
            />
        </Popover>
    );
};

export default EmojiPickerPopover;